import { Timestamp } from "firebase/firestore/lite";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  Dish,
  DishIngredient,
  FirestoreDishDocument,
} from "@dashboard/types/dish";

import { readEntitySource, writeEntitySource } from "./entitySource";

// Converts a firestore dish document to a dish object
export const readDish = (doc: FirestoreDishDocument): Dish => {
  // drop the fields that are not part of the Dish type
  // genDetails is dropped here and not used in the FE.
  const {
    restaurantRef,
    createdAt,
    modifiedAt,
    genDetails,
    source,
    ...dishRest
  } = doc;

  const dish: Dish = {
    ...dishRest,
    source: readEntitySource(source),
    createdAtMillis: createdAt.toMillis(),
    // If the modified at is not known, we fall back to createdAt.
    // When backfilling modifiedAt was backfilled to null instead of current time or createdAt
    // because this allows for creating reminders.
    // We can change this logic later if we want to show a different date.
    modifiedAtMillis: (modifiedAt || createdAt).toMillis(),
  };

  return dish;
};

// Converts a dish object to a firestore dish document
export const writeDish = (
  firestoreRefs: FirestoreRefs,
  dish: Dish,
  isNew: boolean
): FirestoreDishDocument => {
  const updateTs = Timestamp.now();
  const additionalFieldsNewDish = isNew
    ? {
        createdAt: updateTs,
        restaurantRef: firestoreRefs.restaurant(dish.restaurantUuid),
        ingredients: dish.ingredients.map(
          (ingredient) =>
            ({
              ...ingredient,

              // The FE doesn't yet have support for these features, so we default them to false.
              optional: false,
              secret: false,
              substitutableWithIds: [],
            } as DishIngredient)
        ),
      }
    : {};

  // we don't write genDetails or timestamps to the dish object in firestore.
  const { createdAtMillis, modifiedAtMillis, source, ...dishRest } = dish;
  const firestoreDish = {
    ...dishRest,
    ...additionalFieldsNewDish,
    modifiedAt: updateTs,
    source: writeEntitySource(source),
  } as FirestoreDishDocument;

  return firestoreDish;
};
